/**
 * Processes deferred analytics events after returning from Stripe checkout.
 * Detects successful return via URL param, waits for user context, fires stored events, and cleans up.
 */

import type { ClerkOrganization, ClerkUser } from '@saaga/shared/interfaces/auth.interface'
import type {
  DeferredAnalyticsEvent,
  DeferredSubscriptionEvent,
} from '@saaga/shared/interfaces/deferred-analytics.interface'
import { selectOrganization, selectUser } from '@saaga/shared/store/slices/auth.slice'
import { useAppSelector } from '@saaga/shared/store/store'
import { setAnalyticsUserContext, trackEvent } from '@saaga/shared/utils/analytics.util'
import {
  cleanCheckoutParam,
  consumeDeferredAnalyticsEvent,
  isCheckoutReturn,
} from '@saaga/shared/utils/deferred-analytics.util'
import { pushGtmPurchase } from '@saaga/shared/utils/gtm.util'
import { useEffect, useRef } from 'react'

const fireDeferredSubscription = (event: DeferredSubscriptionEvent): void => {
  trackEvent('subscription_started', {
    planName: event.planName,
    isYearly: event.isYearly,
    value: event.value,
    currency: 'USD',
  })
  pushGtmPurchase({
    transactionId: `sub_${event.planName}_${Date.now()}`,
    value: event.value,
    currency: 'USD',
    itemList: [
      {
        itemId: event.planName,
        itemName: `${event.planName} Plan`,
        price: event.value,
        quantity: 1,
      },
    ],
  })
}

const dispatchDeferredEvent = (event: DeferredAnalyticsEvent): void => {
  if (event.type === 'subscription_started') {
    fireDeferredSubscription(event)
    return
  }
  trackEvent('payment_method_added', {})
}

const processDeferredCheckout = (user: ClerkUser, organization: ClerkOrganization | null): void => {
  cleanCheckoutParam()

  const event = consumeDeferredAnalyticsEvent()
  if (!event) return

  setAnalyticsUserContext({
    userId: user.id,
    orgId: organization?.id ?? '',
    orgSlug: organization?.slug ?? '',
    email: user.email,
  })

  dispatchDeferredEvent(event)
}

export const useDeferredBillingTracking = (): void => {
  const user = useAppSelector(selectUser)
  const organization = useAppSelector(selectOrganization)
  const hasProcessedRef = useRef(false)

  useEffect(() => {
    if (hasProcessedRef.current) return
    if (!isCheckoutReturn()) return
    if (!user) return

    hasProcessedRef.current = true
    processDeferredCheckout(user, organization)
  }, [user, organization])
}
